import useIngredients from "@/hooks/useIngredients";
import { useStores } from "@/hooks/useStores";
import clsx from "clsx";
import Beta from "components/Beta";
import InlineLoading from "components/InlineLoading";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";

const allowedTypes = {
  ENERC_KCAL: {
    order: 1,
    label: "Calories",
    parent: true,
    overrideKey: "calories",
  },
  FAT: {
    order: 2,
    label: "Fat",
    parent: true,
    overrideKey: "fat",
  },
  FASAT: {
    order: 3,
    label: "Saturated fat",
    parent: false,
    overrideKey: "saturatedFat",
  },
  FIBTG: {
    order: 4,
    label: "Dietary fibre",
    parent: true,
    overrideKey: "dietaryFibre",
  },
  SUGAR: {
    order: 6,
    label: "Sugars",
    parent: false,
    overrideKey: "sugars",
  },
  "CHOCDF.net": {
    order: 5,
    label: "Carbohydrates",
    parent: true,
    overrideKey: "carbohydrates",
  },
  PROCNT: {
    order: 7,
    label: "Protein",
    parent: true,
    overrideKey: "protein",
  },
  NA: {
    order: 8,
    label: "Sodium",
    parent: true,
    overrideKey: "sodium",
  },
};

const Nutrition = observer(({ recipe }) => {
  const { ingredientsVolumeToMass } = useIngredients(recipe);
  const [nutrition, setNutrition] = useState(null);
  const [nutritionState, setNutritionState] = useState("initial");
  const { authStore } = useStores();
  const currentUser = authStore.currentUser;
  const shouldUseManualNutrition =
    recipe.overrideNutritionalInformation &&
    (recipe.calories ||
      recipe.fat ||
      recipe.protein ||
      recipe.carbohydrates ||
      recipe.saturatedFat ||
      recipe.dietaryFibre ||
      recipe.sugars ||
      recipe.sodium);

  useEffect(() => {
    const fetchNutrition = async () => {
      if (shouldUseManualNutrition) {
        // Use nutrition data from recipe fields
        const recipeNutrition = {
          totalNutrients: {
            ENERC_KCAL: {
              label: "Energy",
              quantity: recipe.calories || 0,
              unit: "kcal",
            },
            FAT: {
              label: "Total lipid (fat)",
              quantity: recipe.fat || 0,
              unit: "g",
            },
            FASAT: {
              label: "Fatty acids, total saturated",
              quantity: recipe.saturatedFat || 0,
              unit: "g",
            },
            FIBTG: {
              label: "Dietary fibre",
              quantity: recipe.dietaryFibre || 0,
              unit: "g",
            },
            SUGAR: {
              label: "Sugars",
              quantity: recipe.sugars || 0,
              unit: "g",
            },
            "CHOCDF.net": {
              label: "Carbohydrates (net)",
              quantity: recipe.carbohydrates || 0,
              unit: "g",
            },
            PROCNT: {
              label: "Protein",
              quantity: recipe.protein || 0,
              unit: "g",
            },
            NA: {
              label: "Sodium",
              quantity: recipe.sodium || 0,
              unit: "mg",
            },
          },
        };

        setNutrition(recipeNutrition);
        setNutritionState("loaded");
        return;
      }

      // Fallback to Edamam API if no recipe nutrition data
      // We only want to have ingredients with a quantity and a type of "ingredient" but isn't overriden
      const ingredientsWithQuantity = ingredientsVolumeToMass.filter(
        (ingredient) =>
          ingredient.quantityRounded > 0 &&
          ingredient.type === "ingredient" &&
          ingredient.overrideNutritionalInformation === false
      );

      // And separately we want to have ingredients with a quantity and a type of "ingredient" but _is_ overriden
      const ingredientsWithQuantityThatAreOverridden =
        ingredientsVolumeToMass.filter(
          (ingredient) =>
            ingredient.quantityRounded > 0 &&
            ingredient.type === "ingredient" &&
            ingredient.overrideNutritionalInformation === true
        );

      const ingredientArray = ingredientsWithQuantity.map((ingredient) => {
        // Return ingredient.sentence with any unnecessary whitespace removed
        return ingredient.sentence.replace(/\s\s+/g, " ");
      });

      const requestData = {
        title: recipe.title,
        ingr: ingredientArray,
      };

      const request = JSON.stringify(requestData);

      if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") {
        console.log("Nutrition: Requested recipe", {
          userId: currentUser.id,
          request,
        });
      }

      try {
        const edamam = await fetch("/api/get-nutrition", {
          method: "POST",
          credentials: "omit",
          headers: {
            "Content-Type": "application/json",
          },
          body: request,
        }).then((response) => response.json());

        if (edamam.error || edamam.status === "error") {
          setNutritionState("error");
          console.log("error", {
            response: edamam.error,
            userId: currentUser.id,
            request,
          });
          return;
        }

        // Kick start with data from Edamam
        const nutrition = edamam;

        // And then loop through ingredientsWithQuantityThatAreOverridden and add their nutritional information to the nutrition object
        ingredientsWithQuantityThatAreOverridden.forEach((ingredient) => {
          const overrideData = ingredient.overrideNutritionalInformationData;

          // All of the overrides are provided as per 100g/ml, so we need to convert them to the quantity of the ingredient's serving size
          const multiplier = ingredient.quantityRounded / 100;

          // Loop through allowedTypes and add the nutritional information to the nutrition object
          Object.keys(allowedTypes).forEach((key) => {
            if (allowedTypes[key].overrideKey) {
              nutrition.totalNutrients[key].quantity =
                nutrition.totalNutrients[key].quantity +
                overrideData[allowedTypes[key].overrideKey] * multiplier;
            }
          });
        });

        if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") {
          console.log("Nutrition: API Loaded", {
            userId: currentUser.id,
            response: edamam,
          });
        }

        setNutrition(nutrition);
        setNutritionState("loaded");
      } catch (error) {
        console.log("Nutrition API Error", {
          error: error.message,
          userId: currentUser.id,
          request,
        });
        setNutritionState("error");
      }
    };

    fetchNutrition();
  }, [recipe]);

  return (
    <>
      {nutritionState === "initial" && (
        <div className="mt-4">
          <InlineLoading message="Calculating..." />
        </div>
      )}{" "}
      {nutritionState === "error" && (
        <div className="mt-4">
          <Beta error />
        </div>
      )}
      {nutritionState === "loaded" && (
        <div className="pb-6 pt-6 lg:pb-0">
          <p className="mb-4 font-body text-sm">
            Nutritional information per serving:
          </p>
          <table className=" !font-normal [&_tbody_tr:nth-child(odd)]:bg-zinc-50 [&_td]:px-4 [&_td]:py-3 [&_tr>td]:border-t">
            <tbody className="border font-body text-base">
              {Object.keys(nutrition.totalNutrients ?? {})
                .filter((key) => Object.keys(allowedTypes).includes(key))
                .sort((a, b) => allowedTypes[a].order - allowedTypes[b].order)
                .map((item, index) => (
                  <tr key={index}>
                    <td className="w-full">
                      <span
                        className={
                          allowedTypes[item].parent ? `font-medium` : "pl-6"
                        }
                      >
                        {allowedTypes[item].label}
                      </span>
                    </td>
                    <td className={clsx("tabular-nums")}>
                      {Math.round(nutrition.totalNutrients[item].quantity)}
                      {nutrition.totalNutrients[item].unit}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="mt-6 flex justify-between gap-4 ">
            <p className="font-body text-xs">
              {shouldUseManualNutrition
                ? "The nutritional information above is an estimate based on the ingredients in this recipe."
                : "The information above is an estimate generated by Edamam based on the ingredients in this recipe."}{" "}
              It isn&apos;t a substitute for a professional nutritionist&apos;s
              advice.
            </p>
            {!shouldUseManualNutrition && (
              <div className="w-[300px]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="400"
                  height="80"
                  fill="none"
                  viewBox="0 0 400 80"
                  className=" h-auto w-full rounded-md border"
                >
                  <g clipPath="url(#clip0_0_3)">
                    <path
                      fill="#fff"
                      d="M385.7 79.5H14.3C6.7 79.5.5 73.3.5 65.7V14.3C.5 6.7 6.7.5 14.3.5h371.4c7.6 0 13.8 6.2 13.8 13.8v51.4c0 7.6-6.2 13.8-13.8 13.8z"
                    ></path>
                    <path
                      fill="#7C0"
                      d="M224.9 42.5h-12.3c-.8 0-1.4.1-1.4.9v7.5c0 .8.6 1.4 1.4 1.4H225c.8 0 1.4.6 1.4 1.4v.3c0 .8-.6 1.4-1.4 1.4h-16.3c-.8 0-1.4-.6-1.4-.9V28.4c0-1.3.6-1.9 1.4-1.9h16.1c.8 0 1.4.6 1.4 1.4v.2c0 .8-.6 1.4-1.4 1.4h-12.2c-.8 0-1.4.6-1.4 1.4v7c0 .8.6 1.4 1.4 1.4h12.3c.8 0 1.4.6 1.4 1.4v.2c0 .9-.6 1.6-1.4 1.6zM232.3 54V27.9c0-.8.6-1.4 1.4-1.4h7.4c3.8 0 6.8 1.2 9.1 3.5s3.4 5.3 3.4 9v4c0 3.7-1.1 6.7-3.4 9s-5.3 3.5-9.1 3.5h-7.4c-.8-.1-1.4-.7-1.4-1.5zm3.9-23v19.9c0 .8.6 1.4 1.4 1.4h3.5c2.7 0 4.8-.9 6.3-2.6 1.5-1.8 2.3-4 2.3-6.8v-4c0-2.7-.8-5-2.3-6.7-1.5-1.8-3.7-2.6-6.3-2.6h-3.5c-.7-.1-1.4.6-1.4 1.4zM274.6 48h-10.1c-.6 0-1.1.4-1.4.9l-2 5.6c-.2.6-.7.9-1.4.9h-.9c-1 0-1.7-1-1.3-2l10.1-26.1c.2-.6.7-.9 1.3-.9h1.4c.6 0 1.1.4 1.3.9l9.9 26.1c.4.9-.3 2-1.3 2h-.9c-.6 0-1.1-.4-1.4-1l-2-5.6c-.2-.5-.7-.8-1.3-.8zm-7.9-3.3h5.6c1 0 1.7-1 1.4-1.9l-4.1-11.2h-.1l-4.1 11.2c-.4.9.3 1.9 1.3 1.9zM291.5 26.5c.6 0 1.1.4 1.3.9l8.9 22.6h.1l8.9-22.6c.2-.6.7-.9 1.3-.9h2.6c.8 0 1.4.6 1.4 1.4V54c0 .8-.6 1.4-1.4 1.4h-1c-.8 0-1.4-.6-1.4-1.4V44l.4-11.8h-.1l-9 22.3c-.2.5-.7.9-1.3.9h-.7c-.6 0-1.1-.4-1.3-.9l-9-22.3h-.1l.4 11.7v10c0 .8-.6 1.4-1.4 1.4h-1c-.8 0-1.4-.6-1.4-1.4v-26c0-.8.6-1.4 1.4-1.4h2.4zM339.5 48h-10.1c-.6 0-1.1.4-1.4.9l-2 5.6c-.2.6-.7.9-1.4.9h-.9c-1 0-1.7-1-1.3-2l10.1-26.1c.2-.6.7-.9 1.3-.9h1.4c.6 0 1.1.4 1.3.9l9.9 26.1c.4.9-.3 2-1.3 2h-.9c-.6 0-1.1-.4-1.4-1l-2-5.6c-.1-.5-.7-.8-1.3-.8zm-7.8-3.3h5.6c1 0 1.7-1 1.4-1.9l-4.1-11.2h-.1l-4.1 11.2c-.4.9.3 1.9 1.3 1.9zM356.4 26.5c.6 0 1.1.4 1.3.9l8.9 22.6h.1l8.9-22.6c.2-.6.7-.9 1.3-.9h2.6c.8 0 1.4.6 1.4 1.4V54c0 .8-.6 1.4-1.4 1.4h-1c-.8 0-1.4-.6-1.4-1.4V44l.4-11.8h-.1l-9 22.3c-.2.5-.7.9-1.3.9h-.7c-.6 0-1.1-.4-1.3-.9l-9-22.3h-.1l.4 11.7v10c0 .8-.6 1.4-1.4 1.4h-1c-.8 0-1.4-.6-1.4-1.4v-26c0-.8.6-1.4 1.4-1.4h2.4zM193.972 51.838c6.044-14.593-.885-31.324-15.479-37.369-14.593-6.045-31.324.885-37.369 15.479-6.044 14.593.885 31.323 15.479 37.368 14.593 6.045 31.324-.885 37.369-15.478z"
                    ></path>
                    <path
                      fill="#F4F7FA"
                      d="M178.7 29.5h-22.3c-.7 0-1.3-.6-1.3-1.3v-.6c0-.7.6-1.3 1.3-1.3h22.3c.7 0 1.3.6 1.3 1.3v.6c0 .8-.6 1.3-1.3 1.3zM178.7 42.5h-22.3c-.7 0-1.3-.6-1.3-1.3v-.5c0-.7.6-1.3 1.3-1.3h22.3c.7 0 1.3.6 1.3 1.3v.5c0 .7-.6 1.3-1.3 1.3zM178.7 55.4h-22.3c-.7 0-1.3-.6-1.3-1.3v-.6c0-.7.6-1.3 1.3-1.3h22.3c.7 0 1.3.6 1.3 1.3v.6c0 .8-.6 1.3-1.3 1.3z"
                    ></path>
                    <path
                      fill="#F4F7FA"
                      d="M162.9 36h-6.4c-.7 0-1.3-.6-1.3-1.3v-.5c0-.7.6-1.3 1.3-1.3h6.4c.7 0 1.3.6 1.3 1.3v.5c0 .7-.6 1.3-1.3 1.3zM163.1 49h-6.5c-.7 0-1.3-.6-1.3-1.3v-.6c0-.7.6-1.3 1.3-1.3h6.5c.7 0 1.3.6 1.3 1.3v.6c-.1.7-.6 1.3-1.3 1.3z"
                      opacity="0.7"
                    ></path>
                    <path
                      fill="#929497"
                      d="M23.5 50.4v4.2h-2V43.2h4.4c1.3 0 2.3.3 3 1 .7.7 1.1 1.5 1.1 2.6s-.4 2-1.1 2.6c-.7.6-1.8.9-3.1.9h-2.3v.1zm0-1.6h2.4c.7 0 1.2-.2 1.6-.5.4-.3.6-.8.6-1.4 0-.6-.2-1.1-.6-1.5-.4-.4-.9-.6-1.5-.6h-2.4v4h-.1zM42 49.2c0 1.1-.2 2.1-.6 2.9-.4.8-.9 1.5-1.7 1.9-.7.5-1.5.7-2.5.7-.9 0-1.7-.2-2.5-.7-.7-.5-1.3-1.1-1.7-1.9-.4-.8-.6-1.8-.6-2.9v-.6c0-1.1.2-2.1.6-2.9.4-.8.9-1.5 1.7-1.9.7-.5 1.5-.7 2.5-.7.9 0 1.7.2 2.5.7.7.4 1.3 1.1 1.7 1.9.4.8.6 1.8.6 2.9v.6zm-2-.6c0-1.3-.2-2.2-.7-2.9-.5-.7-1.2-1-2-1-.9 0-1.5.3-2 1s-.7 1.6-.7 2.8v.7c0 1.2.2 2.2.7 2.9.5.7 1.2 1 2 1 .9 0 1.5-.3 2-1s.7-1.6.7-2.9v-.6zM54.1 51.8l1.8-8.6h2l-2.6 11.4h-1.9l-2.2-8.3-2.2 8.3h-2l-2.6-11.4h2l1.8 8.5 2.2-8.5h1.7l2 8.6zM67.1 49.5h-4.7V53h5.5v1.6h-7.4V43.2h7.4v1.6h-5.4V48h4.7v1.5h-.1zM74.9 50.2h-2.2v4.4h-2V43.2h4c1.3 0 2.3.3 3 .9.7.6 1.1 1.4 1.1 2.6 0 .8-.2 1.4-.6 1.9-.4.5-.9.9-1.5 1.2l2.6 4.7v.1h-2.1l-2.3-4.4zm-2.2-1.6h2c.7 0 1.2-.2 1.6-.5.4-.3.6-.8.6-1.4 0-.6-.2-1.1-.5-1.4-.3-.3-.9-.5-1.6-.5h-2.1v3.8zM88.5 49.5h-4.7V53h5.5v1.6h-7.4V43.2h7.4v1.6h-5.4V48h4.7v1.5h-.1zM92.1 54.6V43.2h3.4c1 0 1.9.2 2.7.7.8.4 1.4 1.1 1.8 1.9.4.8.6 1.8.6 2.8v.6c0 1.1-.2 2-.6 2.8-.4.8-1 1.4-1.8 1.9-.8.4-1.7.7-2.7.7h-3.4zm2-9.8V53h1.3c1 0 1.8-.3 2.4-1 .6-.7.8-1.6.8-2.8v-.6c0-1.2-.3-2.2-.8-2.8-.5-.7-1.3-1-2.3-1h-1.4zM108.9 54.6V43.2h3.9c1.3 0 2.3.3 2.9.8.6.5 1 1.3 1 2.3 0 .5-.1 1-.4 1.4-.3.4-.7.7-1.2 1 .6.2 1.1.5 1.4.9.3.4.5 1 .5 1.7 0 1.1-.3 1.9-1 2.5-.7.6-1.7.9-2.9.9h-4.2v-.1zm2-6.6h1.9c.6 0 1.1-.1 1.4-.4.3-.3.5-.7.5-1.2 0-.6-.2-1-.5-1.2-.3-.2-.8-.4-1.5-.4h-1.9V48h.1zm0 1.5V53h2.2c.6 0 1.1-.2 1.5-.5.3-.3.5-.7.5-1.3 0-1.2-.6-1.8-1.8-1.8h-2.4v.1zM123.5 48.6l2.6-5.4h2.2l-3.8 7.2v4.2h-2v-4.2l-3.8-7.2h2.2l2.6 5.4z"
                    ></path>
                  </g>
                  <defs>
                    <clipPath id="clip0_0_3">
                      <path fill="#fff" d="M0 0H400V80H0z"></path>
                    </clipPath>
                  </defs>
                </svg>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
});

export default Nutrition;
